import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Button, CheckboxGroup, InfoCircle, Modal, Spinner, Tabs } from "@opendate-app/setlist";
import { useSeriesShowContext } from "../SeriesShowApp";
import { ConfirmPickerList } from "../../SeriesIndex/components/ConfirmPicker";
import { toastError, toastSuccess } from "../../../shared/toastHelper";

const WHOLE_SERIES = "series";
const SINGLE_EVENTS = "events";

const firstError = (errors) => {
  if (Array.isArray(errors)) return errors[0];
  if (errors && typeof errors === "object") return Object.values(errors).flat()[0];
  return undefined;
};

const plural = (count, singular, pluralWord = `${singular}s`) => (count === 1 ? singular : pluralWord);

const AddToCollectionModal = ({ open, onClose }) => {
  const { series, teamId, handleAddMemberSeries, handleAttachConfirm, refreshSeries } = useSeriesShowContext();
  const [mode, setMode] = useState(WHOLE_SERIES);
  const [attachableSeries, setAttachableSeries] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [pickedSeriesIds, setPickedSeriesIds] = useState([]);
  // Per picked series with several show types: the ones that join. Default all.
  const [pickedShowTypes, setPickedShowTypes] = useState({});
  const [pickedEvents, setPickedEvents] = useState(new Map());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let ignore = false;
    setMode(WHOLE_SERIES);
    setPickedSeriesIds([]);
    setPickedShowTypes({});
    setPickedEvents(new Map());
    setSubmitting(false);
    setLoadingSeries(true);
    axios
      .get(`/teams/${teamId}/event_series/${series.id}/event_series_memberships.json`)
      .then(({ data }) => {
        if (!ignore) setAttachableSeries(data.event_series || []);
      })
      .catch(() => {
        if (!ignore) toastError("Could not load your series. Please try again.");
      })
      .finally(() => {
        if (!ignore) setLoadingSeries(false);
      });
    return () => {
      ignore = true;
    };
  }, [open, teamId, series.id]);

  const changeMode = (value) => {
    setMode(value);
    setPickedSeriesIds([]);
    setPickedShowTypes({});
    setPickedEvents(new Map());
  };

  const showTypesOf = (id) => attachableSeries.find((candidate) => candidate.id === id)?.show_types || [];
  const chosenShowTypes = (id) => pickedShowTypes[id] ?? showTypesOf(id);
  // Every show type picked sends none, so show types the series adds later join too.
  const showTypesToSend = (id) => {
    const available = showTypesOf(id);
    const chosen = chosenShowTypes(id);
    return available.length === 0 || chosen.length === available.length ? null : chosen;
  };
  const missingShowTypes = mode === WHOLE_SERIES
    && pickedSeriesIds.some((id) => showTypesOf(id).length > 0 && chosenShowTypes(id).length === 0);

  const toggleEvent = (confirm) => {
    setPickedEvents((previous) => {
      const next = new Map(previous);
      if (next.has(confirm.id)) {
        next.delete(confirm.id);
      } else {
        next.set(confirm.id, confirm);
      }
      return next;
    });
  };

  const pickedCount = mode === WHOLE_SERIES ? pickedSeriesIds.length : pickedEvents.size;

  const addAll = async (items, add, label) => {
    const failures = [];
    for (const item of items) {
      try {
        await add(item);
      } catch (e) {
        const reason = firstError(e?.response?.data?.errors);
        failures.push(reason ? `${label(item)} (${reason})` : label(item));
      }
    }
    return failures;
  };

  const submit = async () => {
    if (submitting || pickedCount === 0 || missingShowTypes) return;
    setSubmitting(true);
    const failures = mode === WHOLE_SERIES
      ? await addAll(
        pickedSeriesIds,
        (id) => handleAddMemberSeries(id, showTypesToSend(id)),
        (id) => attachableSeries.find((candidate) => candidate.id === id)?.name || "a series",
      )
      : await addAll(
        [...pickedEvents.values()],
        (confirm) => handleAttachConfirm(confirm.id, { refresh: false }),
        (confirm) => confirm.title,
      );
    const added = pickedCount - failures.length;
    try {
      if (added > 0) await refreshSeries();
    } catch (e) {
      toastError("Could not refresh the collection. Please reload the page.");
    }
    if (added > 0) {
      toastSuccess(mode === WHOLE_SERIES
        ? `${added} ${plural(added, "series", "series")} added. Their future dates join automatically.`
        : `${added} ${plural(added, "event")} added.`);
    }
    if (failures.length > 0) toastError(`Couldn't add: ${failures.join(", ")}.`);
    onClose();
  };

  const seriesItems = attachableSeries.map((candidate) => ({
    value: candidate.id,
    label: candidate.name,
    supportingText: `${candidate.dates_count} ${plural(candidate.dates_count, "date")} now · new dates join automatically`,
  }));

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      icon={false}
      width={600}
      title={`Add to ${series.name}`}
      description="Add a whole series, or pick single events."
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={submitting || pickedCount === 0 || missingShowTypes}
            data-testid="add-to-collection-submit"
          >
            {submitting ? "Adding…" : `Add ${pickedCount || ""}`.trim()}
          </Button>
        </>
      )}
    >
      <div className="d-flex flex-column add-to-collection-body" data-testid="add-to-collection-modal">
        <Tabs
          variant="button-border"
          value={mode}
          onChange={changeMode}
          items={[
            { value: WHOLE_SERIES, label: "Whole series" },
            { value: SINGLE_EVENTS, label: "Single events" },
          ]}
        />
        {mode === WHOLE_SERIES ? (
          <>
            {loadingSeries && <Spinner size="sm" label="Loading series..." />}
            {!loadingSeries && seriesItems.length === 0 && (
              <div className="text-muted" data-testid="add-to-collection-no-series">
                {series.venue
                  ? "No series to add. Published series at this collection's venue show up here."
                  : "No series to add. Your published series show up here."}
              </div>
            )}
            {!loadingSeries && seriesItems.length > 0 && (
              <CheckboxGroup values={pickedSeriesIds} onChange={setPickedSeriesIds} items={seriesItems} />
            )}
            {pickedSeriesIds.filter((id) => showTypesOf(id).length > 0).map((id) => {
              const name = attachableSeries.find((candidate) => candidate.id === id)?.name;
              const chosen = chosenShowTypes(id);
              return (
                <div key={id} data-testid={`add-to-collection-show-types-${id}`}>
                  <div className="font-weight-bold mb-1">{`Show types from ${name}`}</div>
                  <div className="text-muted small mb-2">Only dates of these show types join, so the collection&apos;s passes cover just those.</div>
                  <CheckboxGroup
                    values={chosen}
                    onChange={(values) => setPickedShowTypes((current) => ({ ...current, [id]: values }))}
                    items={showTypesOf(id).map((showType) => ({ value: showType, label: showType }))}
                  />
                  {chosen.length === 0 && <div className="text-danger small mt-1">Pick at least one show type.</div>}
                </div>
              );
            })}
            <div className="d-flex align-items-start text-muted small add-to-collection-note">
              <InfoCircle width={16} height={16} aria-hidden="true" className="mr-2 flex-shrink-0" />
              <span>The series stays its own thing. Adding it here doesn't change its tickets or settings.</span>
            </div>
          </>
        ) : (
          <ConfirmPickerList
            open={open && mode === SINGLE_EVENTS}
            teamId={teamId}
            excludeSeriesId={series.id}
            multiSelect
            selected={pickedEvents}
            onToggle={toggleEvent}
          />
        )}
      </div>
    </Modal>
  );
};

AddToCollectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddToCollectionModal;
